import { useState, useMemo } from "react";
import { 
  Sparkles, Heart, Activity, Zap, Wind, 
  ArrowRight, Shield, ShieldCheck, CheckCircle2, Info, Building2
} from "lucide-react";
import { calculateIMC, classifyIMC } from "@/pages/FormF1/helpers";

/**
 * WowResultsStep
 * 
 * Componente de revelación estética (Factor Wow) post-escaneo facial rPPG.
 * Muestra la Edad Cardiovascular estimada, el Índice de Estrés de Baevsky (0-1000)
 * y solicita una micro-captura basal de Peso y Talla para calibrar el IMC y riesgo.
 */
export default function WowResultsStep({
  results = {},
  userAge = 35,
  initialPeso = "",
  initialTalla = "",
  initialWhatsapp = "",
  companyName = "",
  onFinish,
  saving = false
}) {
  const [peso, setPeso] = useState(initialPeso ? String(initialPeso) : "");
  const [talla, setTalla] = useState(initialTalla ? String(initialTalla) : "");
  const [whatsapp, setWhatsapp] = useState(initialWhatsapp ? String(initialWhatsapp) : "");
  const [showAgeTooltip, setShowAgeTooltip] = useState(false);
  const [showStressTooltip, setShowStressTooltip] = useState(false);

  // Signos vitales extraídos del escaneo rPPG
  const hr = results.heartRate ? Math.round(results.heartRate) : null;
  const spo2 = results.oxygenSaturation ? Math.round(results.oxygenSaturation) : null;
  const hrv = results.heartRateVariability ? Math.round(results.heartRateVariability) : null;
  const vascularAge = results.cardiovascularAge ? Math.round(results.cardiovascularAge) : null;
  const stressVal = results.stressIndex != null ? Math.round(results.stressIndex) : null;

  // Cálculo en tiempo real de IMC
  const imcInfo = useMemo(() => {
    const p = parseFloat(peso);
    const t = parseFloat(talla);
    if (!p || !t || p <= 0 || t <= 0) return null;
    const imcVal = calculateIMC(p, t);
    if (!imcVal) return null;
    const classification = classifyIMC(parseFloat(imcVal));
    return {
      value: imcVal,
      category: classification?.category || classification?.label || "Normal",
      color: classification?.color || "#157346"
    };
  }, [peso, talla]);

  // Interpretación de Estrés Baevsky (0 a 1000)
  const stressInterpretation = useMemo(() => {
    if (stressVal === null) return null;
    const percentage = Math.min(100, Math.max(5, Math.round((stressVal / 1000) * 100)));
    if (stressVal < 150) {
      return { label: "Óptimo / Relajado", color: "#10B981", badgeBg: "#E6F4EA", pct: percentage };
    } else if (stressVal < 300) {
      return { label: "Estrés Moderado", color: "#F59E0B", badgeBg: "#FEF3C7", pct: percentage };
    } else if (stressVal < 500) {
      return { label: "Estrés Elevado", color: "#F97316", badgeBg: "#FFEDD5", pct: percentage };
    }
    return { label: "Estrés Crítico", color: "#EF4444", badgeBg: "#FEE2E2", pct: percentage };
  }, [stressVal]);

  // Comparación de Edad Vascular
  const ageDelta = vascularAge !== null && userAge ? vascularAge - userAge : null;

  const handleSubmit = (skipBasals = false) => {
    if (onFinish) {
      const finalPeso = skipBasals
        ? (initialPeso ? parseFloat(initialPeso) : null)
        : (peso ? parseFloat(peso) : (initialPeso ? parseFloat(initialPeso) : null));
      const finalTalla = skipBasals
        ? (initialTalla ? parseFloat(initialTalla) : null)
        : (talla ? parseFloat(talla) : (initialTalla ? parseFloat(initialTalla) : null));
      const finalWhatsapp = skipBasals
        ? (initialWhatsapp || "")
        : (whatsapp || initialWhatsapp || "");

      let finalBmi = null;
      if (finalPeso && finalTalla) {
        const calculated = calculateIMC(finalPeso, finalTalla);
        if (calculated) finalBmi = parseFloat(calculated);
      }

      onFinish({
        peso: finalPeso,
        talla: finalTalla,
        bmi: finalBmi,
        whatsapp: finalWhatsapp,
        results
      });
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto px-4 py-8 animate-fadeIn" data-testid="wow-results-step">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.35s ease-out;
        }
      `}</style>

      {/* Header corporativo / institucional */}
      {companyName && (
        <div className="flex items-center justify-center gap-1.5 mb-3">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-teal-50 text-teal-800 border border-teal-200/80 shadow-sm">
            <Building2 size={13} className="text-teal-600" />
            {companyName} · Programa de Bienestar
          </span>
        </div>
      )}

      {/* Título de la revelación */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-teal-700 bg-teal-500/10 mb-2">
          <Sparkles size={14} className="text-teal-600" />
          Resultados de tu Chequeo Facial
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#2D2A24] tracking-tight">
          ¡Tu escaneo biométrico se completó con éxito!
        </h1>
        <p className="text-sm text-[#71644C] mt-2 max-w-md mx-auto">
          Analizamos tus pulsaciones arteriales en tiempo real para estimar tu edad cardiovascular y nivel de recuperación.
        </p>
      </div>

      {/* ═══ TARJETA 1: FACTOR WOW (Edad Vascular & Estrés Baevsky) ═══ */}
      <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 shadow-xl border border-[#E2D9CB]/60 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {/* Edad Cardiovascular */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FCFAEE] to-[#F5EFE0] border border-[#E2D9CB]/80 relative overflow-hidden">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-[#71644C]">
                Edad Cardiovascular
              </span>
              <button
                type="button"
                onClick={() => setShowAgeTooltip(!showAgeTooltip)}
                className="text-[#71644C] hover:text-[#00628F] transition-colors"
                title="Información clínica"
              >
                <Info size={14} />
              </button>
            </div>

            {showAgeTooltip && (
              <p className="text-[11px] text-[#71644C] bg-white/95 p-2 rounded-lg border border-[#E2D9CB] mb-2 leading-relaxed">
                Estimación de la edad funcional de tus arterias basada en la rigidez vascular y pulso periférico.
              </p>
            )}

            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-4xl font-black text-[#2D2A24]">
                {vascularAge ?? "--"}
              </span>
              <span className="text-sm font-semibold text-[#71644C]">años</span>
            </div>

            <div className="mt-3 flex items-center gap-1.5">
              {ageDelta !== null && (
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: ageDelta <= 0 ? "#E6F4EA" : "#FEF3C7",
                    color: ageDelta <= 0 ? "#137333" : "#B45309"
                  }}
                >
                  {ageDelta === 0
                    ? `Acorde a tu edad cronológica (${userAge} años)`
                    : ageDelta < 0
                    ? `${Math.abs(ageDelta)} ${Math.abs(ageDelta) === 1 ? 'año' : 'años'} menor que tu edad cronológica (${userAge} años)`
                    : `${ageDelta} ${ageDelta === 1 ? 'año' : 'años'} sobre tu edad cronológica (${userAge} años)`}
                </span>
              )}
            </div>
          </div>

          {/* Estrés Baevsky */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FCFAEE] to-[#F5EFE0] border border-[#E2D9CB]/80 relative overflow-hidden">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-[#71644C]">
                Índice de Estrés
              </span>
              <button
                type="button"
                onClick={() => setShowStressTooltip(!showStressTooltip)}
                className="text-[#71644C] hover:text-[#00628F] transition-colors"
                title="Información clínica"
              >
                <Info size={14} />
              </button>
            </div>

            {showStressTooltip && (
              <p className="text-[11px] text-[#71644C] bg-white/95 p-2 rounded-lg border border-[#E2D9CB] mb-2 leading-relaxed">
                Índice clínico de Baevsky: evalúa la respuesta y el equilibrio de tu sistema nervioso ante la tensión física y emocional.
              </p>
            )}

            <div className="flex items-baseline gap-2 mt-2">
              <span className="text-4xl font-black text-[#2D2A24]">
                {stressVal ?? "--"}
              </span>
              <span className="text-xs font-semibold text-[#71644C]">/ 1000</span>
            </div>

            {stressInterpretation && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] font-bold mb-1">
                  <span style={{ color: stressInterpretation.color }}>
                    {stressInterpretation.label}
                  </span>
                  <span className="text-[#71644C]">{stressInterpretation.pct}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-[#E2D9CB] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${stressInterpretation.pct}%`,
                      backgroundColor: stressInterpretation.color
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trío de Signos Vitales Básicos */}
        <div className="grid grid-cols-3 gap-2 pt-4 border-t border-[#E2D9CB]/70">
          <div className="text-center p-2 rounded-xl bg-slate-50/70 border border-slate-200/50">
            <div className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase text-slate-500 mb-1">
              <Heart size={12} className="text-rose-500" />
              Ritmo
            </div>
            <div className="text-lg font-black text-slate-800">
              {hr ?? "--"}{" "}
              <span className="text-[10px] font-semibold text-slate-500">lpm</span>
            </div>
          </div>

          <div className="text-center p-2 rounded-xl bg-slate-50/70 border border-slate-200/50">
            <div className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase text-slate-500 mb-1">
              <Wind size={12} className="text-teal-600" />
              SpO₂
            </div>
            <div className="text-lg font-black text-slate-800">
              {spo2 ?? "--"}{" "}
              <span className="text-[10px] font-semibold text-slate-500">%</span>
            </div>
          </div>

          <div className="text-center p-2 rounded-xl bg-slate-50/70 border border-slate-200/50">
            <div className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase text-slate-500 mb-1">
              <Zap size={12} className="text-amber-500" />
              HRV
            </div>
            <div className="text-lg font-black text-slate-800">
              {hrv ?? "--"}{" "}
              <span className="text-[10px] font-semibold text-slate-500">ms</span>
            </div>
          </div>
        </div>

        {/* Nota preventiva de bienestar clínico */}
        <div
          className="mt-4 p-3 rounded-2xl bg-amber-50/90 border border-amber-200/80 flex items-start gap-2.5 text-[11px] text-amber-900 leading-relaxed"
          data-testid="wow-clinical-disclaimer"
        >
          <ShieldCheck size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong className="font-bold">Nota preventiva de bienestar:</strong> Los valores estimados mediante escaneo óptico facial (rPPG) constituyen una aproximación referencial y una alerta preventiva temprana. No sustituyen un diagnóstico médico; ante cualquier parámetro fuera de rango, corrobórelo siempre con un dispositivo clínico validado.
          </div>
        </div>
      </div>

      {/* ═══ TARJETA 2: CONFIRMACIÓN DE MEDIDAS FÍSICAS (PESO Y TALLA) ═══ */}
      <div className="bg-white/90 backdrop-blur-md rounded-3xl p-6 shadow-xl border border-[#E2D9CB]/60 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-700">
            <Activity size={16} />
          </div>
          <h2 className="text-base font-bold text-[#2D2A24]">
            Confirmación de Medidas Físicas
          </h2>
        </div>
        <p className="text-xs text-[#71644C] mb-4 leading-relaxed">
          Verifica o ajusta tu peso y estatura para el cálculo final de tu Índice de Masa Corporal (IMC) y calibración metabólica:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-[#2D2A24] mb-1.5">
              Peso actual (kg)
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="30"
                max="250"
                value={peso}
                onChange={(e) => setPeso(e.target.value)}
                placeholder="Ej. 72.5"
                className="w-full px-4 py-3 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                data-testid="onboarding-peso-input"
              />
              <span className="absolute right-3.5 top-3.5 text-xs font-bold text-[#71644C]">
                kg
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-[#2D2A24] mb-1.5">
              Estatura (cm)
            </label>
            <div className="relative">
              <input
                type="number"
                step="1"
                min="100"
                max="230"
                value={talla}
                onChange={(e) => setTalla(e.target.value)}
                placeholder="Ej. 175"
                className="w-full px-4 py-3 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                data-testid="onboarding-talla-input"
              />
              <span className="absolute right-3.5 top-3.5 text-xs font-bold text-[#71644C]">
                cm
              </span>
            </div>
          </div>
        </div>

        {/* Badge interactivo de IMC en tiempo real */}
        {imcInfo && (
          <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between animate-fadeIn">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-700">Tu IMC Estimado:</span>
              <span className="text-sm font-black text-slate-900">{imcInfo.value} kg/m²</span>
            </div>
            <span
              className="text-xs font-bold px-2.5 py-0.5 rounded-full"
              style={{
                backgroundColor: `${imcInfo.color}20`,
                color: imcInfo.color
              }}
            >
              {imcInfo.category}
            </span>
          </div>
        )}

        {/* Confirmación de WhatsApp para el Coach de Salud IA */}
        <div className="mt-4 pt-4 border-t border-[#E2D9CB]/60">
          <label className="block text-xs font-bold text-[#2D2A24] mb-1.5">
            WhatsApp vinculado a tu Coach IA
          </label>
          <input
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="+507 6000-0000"
            className="w-full px-4 py-3 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
            data-testid="onboarding-whatsapp-input"
          />
          <p className="text-[11px] text-[#71644C] mt-1">
            Con este número tu Coach IA de WhatsApp asociará tus biomarcadores recién escaneados.
          </p>
        </div>
      </div>

      {/* ═══ ACCIÓN PRINCIPAL: VER MI TABLERO COMPLETO ═══ */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => handleSubmit(false)}
          disabled={saving}
          className="w-full cursor-pointer py-4 px-6 rounded-2xl bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/25 hover:shadow-xl hover:scale-[1.01] active:scale-[0.98] transition-all text-white font-bold text-base flex items-center justify-center gap-2 border-none disabled:opacity-50"
          data-testid="wow-finish-btn"
        >
          {saving ? (
            <span>Guardando tus biomarcadores...</span>
          ) : (
            <>
              <span>Ver Mi Tablero Completo</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => handleSubmit(true)}
            className="text-xs font-semibold text-[#71644C] hover:text-[#00628F] hover:underline transition-colors"
            data-testid="wow-skip-basals-btn"
          >
            Confirmar con medidas ingresadas e ir al tablero
          </button>
        </div>
      </div>
    </div>
  );
}
